/**
 * Activity translations mapping
 *
 * Maps activity names to content field keys for the content-activity-list
 * translations in Strapi. This allows activities to be translated based
 * on the locale without modifying backend data.
 *
 * The mapping uses ActivityName (from ca-activity-list) to find the
 * corresponding translation field in content-activity-list.
 */

/**
 * Map of ActivityName to content field key
 * Keys: Exact ActivityName strings from ca-activity-list (Snowflake data)
 * Values: Field names in content-activity-list schema
 */
const ACTIVITY_NAME_TO_CONTENT_KEY = {
    // === AVIATION FUELS ===
    'Aviation spirit - kWh (Net CV)': 'aviationSpirit',
    'Aviation spirit - litres': 'aviationSpiritLitres',
    'Aviation turbine fuel - kWh (Net CV)': 'aviationTurbineFuel',
    'Aviation turbine fuel - litres': 'aviationTurbineFuelLitres',
    'Avtur - kWh (Net CV)': 'avtur',
    'Avtur - litres': 'avturLitres',

    // === BIODIESEL ===
    'Biodiesel (HVO) - kWh (Net CV)': 'biodieselHvo',
    'Biodiesel (HVO) - litres': 'biodieselHvoLitres',
    'Biodiesel (ME) - kWh (Net CV)': 'biodieselMe',
    'Biodiesel (ME) - litres': 'biodieselMeLitres',
    'Biodiesel (ME) from used cooking oil - kWh (Net CV)': 'biodieselMeCookingOil',
    'Biodiesel (ME) from used cooking oil - litres': 'biodieselMeCookingOilLitres',
    'Biodiesel (ME) from tallow - kWh (Net CV)': 'biodieselMeTallow',
    'Biodiesel (ME) from tallow - litres': 'biodieselMeTallowLitres',

    // === BIOETHANOL ===
    'Bioethanol - kWh (Net CV)': 'bioethanol',
    'Bioethanol - litres': 'bioethanolLitres',

    // === BIOGAS ===
    'Biogas - kWh (Gross CV)': 'biogasKwh',
    'Biogas - kWh (Net CV)': 'biogasKwh',
    'Biogas - tonnes': 'biogasTonnes',

    // === BIOMETHANE ===
    'Biomethane (compressed) - GJ': 'biomethaneCompressedGj',
    'Biomethane (compressed) - kWh (Net CV)': 'biomethaneCompressed',
    'Biomethane (liquified) - GJ': 'biomethaneLiquifiedGj',
    'Biomethane (liquified) - kWh (Net CV)': 'biomethaneLiquified',

    // === BIOPROPANE ===
    'Biopropane - GJ': 'biopropaneGj',
    'Biopropane - litres': 'biopropaneLitres',

    // === BURNING OIL ===
    'Burning oil - kWh (Gross CV)': 'burningOilKwh',
    'Burning oil - kWh (Net CV)': 'burningOilKwhNet',
    'Burning oil - litres': 'burningOilLitres',

    // === BUTANE ===
    'Butane - kWh (Gross CV)': 'butaneKwh',
    'Butane - kWh (Net CV)': 'butane',
    'Butane - litres': 'butaneKwh',

    // === COAL ===
    'Coal (industrial) - kWh (Net CV)': 'coalIndustrialKwh',
    'Coal (industrial) - tonnes': 'coalIndustrialTonnes',
    'Coking coal - kWh (Net CV)': 'cokingCoalKwh',
    'Coking coal - tonnes': 'cokingCoalTonnes',

    // === COMPRESSED NATURAL GAS ===
    'Compressed natural gas (CNG) - kWh (Net CV)': 'compressedNaturalGasKwh',
    'Compressed natural gas (CNG) - tonnes': 'compressedNaturalGasTonnes',

    // === DIESEL ===
    'Diesel (100% mineral diesel) - kWh (Net CV)': 'dieselMineral',
    'Diesel (100% mineral diesel) - litres': 'dieselMineralLitres',

    // === ELECTRICITY ===
    'Electricity (national grid) - kWh': 'electricityGridKwh',
    'Electricity (certified renewable sources) - kWh': 'electricityRenewablesKwh',

    // === FUEL OIL ===
    'Fuel oil - kWh (Gross CV)': 'fuelOilKwh',
    'Fuel oil - kWh (Net CV)': 'fuelOilKwhNet',
    'Fuel oil - litres': 'fuelOilLitres',
    'Fuel oil - tonnes': 'fuelOilLitres',

    // === GAS OIL ===
    'Gas oil - kWh (Gross CV)': 'gasOilKwh',
    'Gas oil - kWh (Net CV)': 'gasOilKwhNet',
    'Gas oil - litres': 'gasOilLitres',

    // === GRASS / STRAW ===
    'Grass / straw - kWh': 'grassStrawKwh',
    'Grass / straw - tonnes': 'grassStrawTonnes',

    // === HEAT AND STEAM ===
    'District heat and steam (grid) - kWh': 'heatSteamDistrictKwh',
    'Renewable heat and steam (certified) - kWh': 'heatSteamRenewableKwh',

    // === LANDFILL GAS ===
    'Landfill gas - kWh (Net CV)': 'landfillGasKwh',
    'Landfill gas - tonnes': 'landfillGasTonnes',

    // === LPG (Liquid Petroleum Gas) ===
    'Liquid petroleum gas (LPG) - kWh (Net CV)': 'liquidPetroleumGasKwh',
    'Liquid petroleum gas (LPG) - litres': 'liquidPetroleumGasKwh',
    'Liquid petroleum gas (LPG) - tonnes': 'liquidPetroleumGasTonnes',

    // === LNG (Liquefied Natural Gas) ===
    'Liquefied natural gas (LNG) - kWh (Net CV)': 'liquefiedNaturalGasKwh',
    'Liquefied natural gas (LNG) - tonnes': 'liquefiedNaturalGasTonnes',

    // === LUBRICANTS ===
    'Lubricants - kWh (Net CV)': 'lubricantsKwh',
    'Lubricants - litres': 'lubricants',

    // === MARINE FUELS ===
    'Marine fuel oil - kWh (Net CV)': 'marineFuelOil',
    'Marine fuel oil - litres': 'marineFuelOilLitres',
    'Marine gas oil - kWh (Net CV)': 'marineGasOil',
    'Marine gas oil - litres': 'marineGasOilLitres',

    // === NAPHTHA ===
    'Naphtha - kWh (Net CV)': 'naphthaKwh',
    'Naphtha - litres': 'naphtha',
    'Naphtha - tonnes': 'naphtha',

    // === NATURAL GAS ===
    'Natural gas (100% mineral blend) - kWh (Net CV)': 'naturalGasMineralKwh',
    'Natural gas (100% mineral blend) - tonnes': 'naturalGasMineral',

    // === OTHER PETROLEUM GAS ===
    'Other petroleum gas - kWh (Net CV)': 'otherPetroleumGasKwh',
    'Other petroleum gas - litres': 'otherPetroleumGas',
    'Other petroleum gas - tonnes': 'otherPetroleumGas',

    // === PETROLEUM COKE ===
    'Petroleum coke - kWh (Net CV)': 'petroleumCokeKwh',
    'Petroleum coke - tonnes': 'petroleumCokeTonnes',

    // === PETROL ===
    'Petrol (100% mineral petrol) - kWh (Net CV)': 'petrolMineral',
    'Petrol (100% mineral petrol) - litres': 'petrolMineralLitres',

    // === PROCESSED FUEL OILS ===
    'Processed fuel oils (distillate oil) - kWh (Net CV)': 'processedFuelOilsDistilledLitres',
    'Processed fuel oils (distillate oil) - litres': 'processedFuelOilsDistilledLitres',
    'Processed fuel oils (residual fuel oil) - kWh (Net CV)': 'processedFuelOilsResidualLitres',
    'Processed fuel oils (residual fuel oil) - litres': 'processedFuelOilsResidualLitres',
    'Processed fuel oils (residual oil) - litres': 'processedFuelOilsResidualLitres',

    // === PROPANE ===
    'Propane - kWh (Net CV)': 'propaneKwh',
    'Propane - litres': 'propane',

    // === REFINERY ===
    'Refinery miscellaneous - kWh (Net CV)': 'refineryMiscKwh',
    'Refinery miscellaneous - tonnes': 'refineryMiscTonnes',

    // === RESIDUAL FUEL OIL ===
    'Residual fuel oil - kWh (Net CV)': 'processedFuelOilsResidualLitres',
    'Residual fuel oil - tonnes': 'processedFuelOilsResidualLitres',

    // === SOLID SMOKELESS FUEL ===
    'Solid smokeless fuel - kWh (Net CV)': 'solidFuels',
    'Solid smokeless fuel - tonnes': 'solidFuels',

    // === SPIRIT TYPE FUEL ===
    'Spirit type fuel - kWh (Net CV)': 'liquidFuels',
    'Spirit type fuel - litres': 'liquidFuels',

    // === WASTE OILS ===
    'Waste oils - kWh (Net CV)': 'wasteOilsKwh',
    'Waste oils - litres': 'wasteOils',
    'Waste oils - tonnes': 'wasteOils',

    // === WOOD ===
    'Wood chips - kWh': 'woodChipsKwh',
    'Wood chips - tonnes': 'woodChipsTonnes',
    'Wood logs - kWh': 'woodLogsKwh',
    'Wood logs - tonnes': 'woodLogsTonnes',
    'Wood pellets - kWh': 'woodPelletsKwh',
    'Wood pellets - tonnes': 'woodPelletsTonnes',
};

/**
 * Get the content field key for an activity name
 *
 * @param {string} activityName - The ActivityName from the database
 * @returns {string|null} Content field key or null if not found
 */
export function getActivityContentKey(activityName) {
    if (!activityName) return null;
    return ACTIVITY_NAME_TO_CONTENT_KEY[activityName] || null;
}

/**
 * Get translated activity name
 *
 * @param {Object} content - Content from useContentActivityList
 * @param {string} _activityId - The ActivityId (unused, kept for API compatibility)
 * @param {string} activityName - The original ActivityName
 * @returns {string} Translated name or original name if not found
 */
export function getActivityName(content, _activityId, activityName) {
    if (!content || !activityName) return activityName || '';

    const contentKey = getActivityContentKey(activityName);
    if (contentKey && content[contentKey]) {
        return content[contentKey];
    }

    return activityName;
}

/**
 * Check if an activity has a translation mapping
 *
 * @param {string} activityName - The ActivityName
 * @returns {boolean} True if a mapping exists
 */
export function hasActivityMapping(activityName) {
    return !!(activityName && ACTIVITY_NAME_TO_CONTENT_KEY[activityName]);
}

// Export the mapping for direct access if needed
export { ACTIVITY_NAME_TO_CONTENT_KEY };
